import serverVars from 'server-vars';
import { fetchQuery_DEPRECATED as fetchQuery, graphql, Environment } from 'react-relay/legacy';
import widgetLoader from 'dibs-widget-loader';

import './styles/recaptcha.scss';

import {
    recaptchaFeatureFlagQuery$data,
    recaptchaFeatureFlagQuery,
} from './__generated__/recaptchaFeatureFlagQuery.graphql';

const NODE_RECAPTCHA_SITE_KEY_NAME = 'NODE_RECAPTCHA_SITE_KEY';
const NODE_RECAPTCHA_SITE_KEY_V3_NAME = 'NODE_RECAPTCHA_SITE_KEY_V3';

const sitekey = serverVars.get(NODE_RECAPTCHA_SITE_KEY_NAME);
const sitekeyv3 = serverVars.get(NODE_RECAPTCHA_SITE_KEY_V3_NAME);
const elId = `dibs-recaptcha`;

const query = graphql`
    query recaptchaFeatureFlagQuery(
        $featureFlag: String = ""
        $featureFlagV3: String = ""
        $hasFeatureFlag: Boolean!
        $hasFeatureFlagV3: Boolean!
    ) {
        viewer {
            featureFlag(feature: $featureFlag) @include(if: $hasFeatureFlag)
            v3Enabled: featureFlag(feature: $featureFlagV3) @include(if: $hasFeatureFlagV3)
        }
    }
`;

const isLoaded = (): boolean =>
    typeof window !== 'undefined' &&
    typeof window.grecaptcha !== 'undefined' &&
    typeof window.grecaptcha.render === 'function';

const isReady = new Promise<void>(resolve => {
    if (isLoaded()) {
        resolve();
    } else {
        const interval = setInterval(() => {
            if (isLoaded()) {
                clearInterval(interval);
                resolve();
            }
        }, 300);
    }
});

async function setUpCaptcha(): Promise<void> {
    if (document.getElementById(elId)) {
        return;
    }
    await widgetLoader.load('recaptcha');
    await isReady;

    // put callbacks on window or else functions get gc'd (i think?)
    window.dibsCaptchaCallbacks = { reject: () => {}, resolve: () => {} };

    const node = document.createElement('div');
    node.setAttribute('id', elId);
    node.setAttribute('data-sitekey', sitekey);
    document.body.appendChild(node);
    window.grecaptcha.render(node, {
        sitekey,
        size: 'invisible',
        callback: token => {
            window.dibsCaptchaCallbacks.resolve(token);
            window.grecaptcha.reset();
        },
        'error-callback': () => {
            window.dibsCaptchaCallbacks.reject();
            window.grecaptcha.reset();
        },
    });
}

const loadCaptcha = async (recaptchaV3SiteKey: string): Promise<void> => {
    await widgetLoader.load('recaptchaEnterprise', { recaptchaV3SiteKey });
    await new Promise<void>(resolve => {
        window.grecaptcha.enterprise.ready(() => resolve());
    });
};

export type SupportedMFATypes = 'CODE'[] | null; // UserLoginMutation supportedMFATypes arg

export type CaptchaResponse = {
    // null if captcha is not enabled via feature flag
    captchaToken: string | null;
    // only set when using recaptcha v3
    captchaKey: string | null;
    mfaVerificationCode?: string | null;
    supportedMFATypes?: SupportedMFATypes | null;
};

async function recaptchaV3(
    args?: {
        action?: string;
        userName?: string | null;
        mfaVerificationCode?: string | null;
        supportedMFATypes?: SupportedMFATypes;
    },
    TMP_siteKey?: string
): Promise<CaptchaResponse> {
    const recaptchaV3SiteKey = TMP_siteKey || sitekeyv3;
    await loadCaptcha(recaptchaV3SiteKey);
    const {
        action = 'noAction',
        userName = '',
        mfaVerificationCode = null,
        supportedMFATypes = null,
    } = args || {};
    const captchaPayload = { action, username: userName };
    const captchaToken = await window.grecaptcha.enterprise.execute(
        recaptchaV3SiteKey,
        captchaPayload
    );
    let actualMFAVerificationCode = mfaVerificationCode;
    if (mfaVerificationCode === '') {
        // must set this to `null` otherwise BE will kick
        // off MFA flow even if it's just an empty string
        actualMFAVerificationCode = null;
    }
    return {
        captchaToken,
        captchaKey: recaptchaV3SiteKey,
        mfaVerificationCode: actualMFAVerificationCode,
        supportedMFATypes,
    };
}

/**
 * Executes recaptcha,
 * relayEnvironment only needed if featureFlag is provided
 */
async function executeCaptcha(args?: {
    featureFlag?: string;
    featureFlagV3?: string;
    action?: string;
    userName?: string | null; // MFA uses email or phone number
    mfaVerificationCode?: string | null;
    supportedMFATypes?: SupportedMFATypes;
    relayEnvironment?: Environment;
}): Promise<CaptchaResponse> {
    const {
        featureFlag,
        featureFlagV3,
        relayEnvironment,
        action,
        userName,
        mfaVerificationCode,
        supportedMFATypes,
    } = args || {};
    if ((featureFlag || featureFlagV3) && relayEnvironment) {
        const result: recaptchaFeatureFlagQuery$data = await fetchQuery<recaptchaFeatureFlagQuery>(
            relayEnvironment,
            query,
            {
                featureFlag,
                featureFlagV3,
                hasFeatureFlag: !!featureFlag,
                hasFeatureFlagV3: !!featureFlagV3,
            }
        );
        const viewer = result?.viewer;
        // if a v3 flag is passed and it is enabled use v3 enterprise
        if (featureFlagV3 && viewer?.v3Enabled) {
            // if a v3 flag is passed and it is enabled use v3 enterprise
            return await recaptchaV3({ action, userName, mfaVerificationCode, supportedMFATypes });
        }
        // else if a v3 flag is passed and it is not enabled return null
        if (featureFlag && !viewer?.featureFlag) {
            return { captchaToken: null, captchaKey: null };
        }
    }
    // no flag is passed or v2 is and it is not enabled return null
    await setUpCaptcha();
    return await new Promise((resolve, reject) => {
        window.dibsCaptchaCallbacks.resolve = captchaToken =>
            resolve({ captchaToken, captchaKey: null });
        window.dibsCaptchaCallbacks.reject = reject;
        window.grecaptcha.execute();
    });
}

if (typeof window !== 'undefined') {
    // for easy testing from console
    // @ts-ignore so we don't have declare the `CaptchaResponse` in the global scope
    window.recaptchaV3 = recaptchaV3;
    // @ts-ignore so we don't have declare the `CaptchaResponse` in the global scope
    window.executeCaptcha = executeCaptcha;
}

export {
    NODE_RECAPTCHA_SITE_KEY_NAME,
    NODE_RECAPTCHA_SITE_KEY_V3_NAME,
    executeCaptcha,
    recaptchaV3,
};
